/* import __COLOCATED_TEMPLATE__ from './progress-slider.hbs'; */
import Component from '@ember/component';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { htmlSafe } from '@ember/template';

/*
### ProgressSlider component

The progress slider component contains the logic for the video player slider and volume bar slider.
This component is currently only used with video-player, though it is extracted to be video-player agnostic.

Usage:

@hbs
{{progress-slider dragContainer=element
                  onClick=(action onClick)
                  onDrag=(action onDrag)
                  progress=(readonly progress)
                  mouseDown=(action pause)
                  mouseUp=(action play)
                  role="slider"
                  valueMin="0"
                  currentValue=sliderValue
                  valueMax=duration}}

### Parameters
 * @param {Action} [onClick] Action fired after clicking on the slider. Sends the action with the event and progress of track (where the track was clicked. see calculateProgress)
 * @param {Action} [onDrag] Action fired after dragging the sliderThumb. Sends the action with the event and progress of track (where the track was clicked. see calculateProgress)
 * @param {Element} [dragContainer] Element object to add the drag event handlers too. This allows holding the drag for the slider thumb to anywhere in the dragContainer.
 * @param {Number} [progress] (readonly) A fraction that represents how far into the slider the thumb is (see progressStyle)
 * @param {Number} [minValue=0] minimum value for the progress slider
 * @param {Number} [maxValue] maximum value for the progress slider
 * @param {number} [currentValue] starting value for the progress slider
*/

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend({
  attributeBindings: ['role', 'aria-valuenow', 'aria-valuemax', 'aria-valuemin'],
  classNames: ['progress-slider'],
  role: 'slider',
  sliderTrack: computed('element', function () {
    return this.element.getElementsByClassName('progress-slider__track')[0];
  }),
  // DO NOT set within this component. It should only be updated from the parent component through action handlers.
  progress: 0,
  minValue: 0,
  maxValue: 1,
  currentValue: 0,
  'aria-valuemin': alias('minValue'),
  'aria-value-max': alias('maxValue'),
  'aria-valuenow': alias('currentValue'),
  progressStyle: computed('progress', function () {
    const percent = this.progress * 100;
    return htmlSafe(`width: ${percent}%`);
  }),
  // Calculates how much the thumb has moved from the left end of the slider track at the end of a click / drag event
  calculateProgress(e) {
    const domRect = this.get('sliderTrack').getBoundingClientRect();
    const trackWidth = domRect.width;
    let distanceFromStartOfTrack = e.pageX - domRect.x;
    // clicked/dragged before the start of the track
    if (distanceFromStartOfTrack < 0) {
      distanceFromStartOfTrack = 0;
      // clicked/dragged past the end of the track
    } else if (distanceFromStartOfTrack > trackWidth) {
      distanceFromStartOfTrack = trackWidth;
    }
    return distanceFromStartOfTrack / trackWidth;
  },
  click(e) {
    const progress = this.calculateProgress(e);
    this.onClick(e, progress);
  },
  // Boolean for whether the slider thumb is being dragged
  isDragging: false,
  sliderThumb: computed('element', function () {
    return this.element.getElementsByClassName('progress-slider__thumb')[0];
  }),
  didInsertElement(...args) {
    this._super(...args);

    // Set the handlers so they can be referenced later for teardown
    this.setDragHandlers();
    this.dragContainer.addEventListener('touchstart', this.dragStart, false);
    this.dragContainer.addEventListener('touchend', this.dragEnd, false);
    this.dragContainer.addEventListener('touchmove', this.drag, false);
    this.dragContainer.addEventListener('mousedown', this.dragStart, false);
    this.dragContainer.addEventListener('mouseup', this.dragEnd, false);
    this.dragContainer.addEventListener('mousemove', this.drag, false);
  },
  willDestroyElement(...args) {
    this._super(...args);
    this.dragContainer.removeEventListener('touchstart', this.dragStart);
    this.dragContainer.removeEventListener('touchend', this.dragEnd);
    this.dragContainer.removeEventListener('touchmove', this.drag);
    this.dragContainer.removeEventListener('mousedown', this.dragStart);
    this.dragContainer.removeEventListener('mouseup', this.dragEnd);
    this.dragContainer.removeEventListener('mousemove', this.drag);
  },
  // Handlers for dragging the slider thumb
  setDragHandlers() {
    this.dragStart = e => {
      if (e.target === this.sliderThumb) {
        this.set('isDragging', true);
      }
    };
    this.dragEnd = () => {
      this.set('isDragging', false);
    };
    this.drag = e => {
      if (this.isDragging) {
        e.preventDefault();
        const progress = this.calculateProgress(e);
        this.onDrag(e, progress);
      }
    };
  }
});